import {
  Column,
  CustomDialog,
  DialogContent,
  DialogTrigger,
  Grid,
  Image,
  Link,
  MarkdownRenderer,
  Text,
} from '@aurora-components';
import { type FragmentType, getFragmentData } from '@gql';
import type { CloudinaryCropResize, CloudinaryGravity } from 'components/aurora/image/types';
import type { Maybe, VildarPageComponentsContentSectionBlockCta } from 'gql/graphql';
import { PlayVideoIcon } from 'icons/components';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { convertDate } from 'utils/convertDate';
import { getImage } from 'utils/get-image';
import { CenterBox, ContentContainer, ImageWrapper, VideoWrapper } from './content-section.styles';
import { ContentSectionFragment } from './content-section-gql';
import type { ContentSectionDialogTypes, ContentVariants } from './types';

const ContentSectionDialog = ({ trigger, content }: ContentSectionDialogTypes) => (
  <CustomDialog>
    <DialogTrigger>
      <VideoWrapper>
        {trigger}
        <PlayVideoIcon />
      </VideoWrapper>
    </DialogTrigger>
    <DialogContent>{content}</DialogContent>
  </CustomDialog>
);

const DynamicVideoPlayer = dynamic(() => import('@aurora-components').then((mod) => mod.VideoPlayer));

const getLink = (locale: string, cta?: Maybe<VildarPageComponentsContentSectionBlockCta>, type?: string) => {
  if (!cta) return { linkTitle: '', linkHref: '' };
  if (cta.internal_linkConnection?.edges?.length) {
    const { title, url } = cta.internal_linkConnection?.edges[0]?.node || {};
    return { linkTitle: title || '', linkHref: `${locale === 'en' ? '/en/' : '/'}${type || ''}${url}` || '' };
  }

  const linkTitle = cta?.external_link?.title;
  const linkHref = cta?.external_link?.href;
  return { linkTitle: linkTitle || '', linkHref: linkHref || '' };
};
type Props = FragmentType<typeof ContentSectionFragment> & { contentPost?: any };

export function ContentSection(props: Props) {
  const { content_section } = getFragmentData(ContentSectionFragment, props);
  const { locale, slug } = useParams() as { locale: string; slug: string };

  if (!content_section) return null;

  let contentPostData = null;
  let assetConnection = null;
  contentPostData = content_section.content_postConnection?.edges![0]?.node;

  let { imgConnection, alt_text, gravity, crop_resize } = content_section?.image || {};

  if (contentPostData) {
    const postImage = contentPostData?.image?.[0] || {};

    assetConnection = postImage.assetConnection;
    crop_resize = postImage.crop_resize;
    gravity = postImage.gravity;
  }

  const imageData = getImage(assetConnection || imgConnection);

  const hasInternalLinkConnection = !!content_section.content_postConnection?.totalCount;

  const { linkTitle, linkHref } = getLink(locale, content_section?.cta, slug);

  const hasLink = !!linkTitle && !!linkHref;

  const { date, show_date, display_title, short_description } =
    content_section?.content_postConnection?.edges![0]?.node! || {};

  const sectionDate = convertDate(locale, date);
  const sectionVariant = content_section.variant as ContentVariants;

  const { url, autoplay, controls } = content_section?.video || {};
  const isVideoVariant = sectionVariant === 'video';
  const isLogoVariant = sectionVariant === 'logo';
  const layout = content_section.layout;

  return (
    <ContentContainer $variant={sectionVariant}>
      <Grid $fullHeight>
        {isVideoVariant ? (
          <Column $span={{ md: 7, xs: 12 }}>
            <ContentSectionDialog
              content={<DynamicVideoPlayer autoplay={!!autoplay} controls={!!controls} url={url ?? ''} />}
              trigger={
                imageData ? (
                  <Image
                    imageId={imageData?.id || ''}
                    priority={true}
                    alt={alt_text || ''}
                    imageSuffix={imageData?.suffix || ''}
                    gravity={gravity as CloudinaryGravity}
                    crop_resize={crop_resize as CloudinaryCropResize}
                    objectFit="cover"
                  />
                ) : (
                  <></>
                )
              }
            />
          </Column>
        ) : (
          !isLogoVariant && (
            <Column $span={{ md: 5, xs: 12 }}>
              {!!content_section?.image && (
                <ImageWrapper>
                  <Image
                    imageId={imageData?.id || ''}
                    priority={true}
                    alt={alt_text || ''}
                    imageSuffix={imageData?.suffix || ''}
                    gravity={gravity as CloudinaryGravity}
                    crop_resize={crop_resize as CloudinaryCropResize}
                  />
                </ImageWrapper>
              )}
            </Column>
          )
        )}

        {!isLogoVariant && (
          <Column
            $span={{
              md: isVideoVariant ? 5 : 7,
              xs: 12,
            }}
            $verticalAlign="center"
          >
            <CenterBox $variant={sectionVariant} $hasLink={hasLink}>
              <CenterBox $variant={sectionVariant} $hasLink={hasLink} $noMargin>
                {!isVideoVariant && !!show_date && sectionDate && (
                  <Text $variant="small" $color="primary-base100">
                    {sectionDate}
                  </Text>
                )}

                {content_section.headline && (
                  <MarkdownRenderer
                    $variant="h4"
                    $color="primary-base100"
                    $paddingBottom="h3"
                    $noBalancer
                    markdown={content_section.headline ?? display_title}
                  />
                )}

                {content_section?.paragraph && (
                  <MarkdownRenderer
                    $variant="smallparag"
                    $color="primary-base100"
                    $noBalancer
                    markdown={content_section.paragraph ?? short_description}
                  />
                )}

                {hasLink && (
                  <Link
                    title={content_section?.cta?.label ?? linkTitle}
                    url={linkHref}
                    target={hasInternalLinkConnection ? '_self' : '_blank'}
                  />
                )}
              </CenterBox>
            </CenterBox>
          </Column>
        )}

        {isLogoVariant && (
          <Column
            $span={{
              md: 12,
              xs: 12,
            }}
          >
            {!!content_section?.image && (
              <ImageWrapper $variant={sectionVariant} $layout={layout}>
                <Image
                  imageId={imageData?.id || ''}
                  priority={true}
                  alt={alt_text || ''}
                  imageSuffix={imageData?.suffix || ''}
                  gravity={gravity as CloudinaryGravity}
                  crop_resize={crop_resize as CloudinaryCropResize}
                  objectFit="cover"
                />
              </ImageWrapper>
            )}
            {content_section.headline && (
              <CenterBox $variant={sectionVariant}>
                <MarkdownRenderer
                  $variant="h3"
                  $textAlign="center"
                  $color="primary-base100"
                  $noBalancer
                  markdown={content_section.headline ?? display_title}
                />
              </CenterBox>
            )}
          </Column>
        )}
      </Grid>
    </ContentContainer>
  );
}
